import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Eye, Loader2, Mail, MousePointerClick, Send } from "lucide-react";

const KIND_LABELS = {
  treinamento_vencimento: { title: "Vencimento de treinamento", text: "Avisa o cliente antes do treinamento perder a validade." },
  plano_acao_vencimento: { title: "Vencimento de plano de ação", text: "Comunica as ações com prazo e envia o link do portal para preenchimento." },
  relatorio_visita: { title: "Relatório de visita", text: "Envia o resumo da visita técnica ao contato do cliente." },
  proposta_link: { title: "Link da proposta", text: "Gera o link público seguro da proposta e envia ao cliente." },
};

const parseDays = (value) => value.split(/[^0-9]+/).map((n) => parseInt(n, 10)).filter((n) => Number.isInteger(n) && n >= 0 && n <= 365);

export const EmailAutomationPanel = () => {
  const [config, setConfig] = useState(null);
  const [trainingDays, setTrainingDays] = useState("");
  const [planDays, setPlanDays] = useState("");
  const [clients, setClients] = useState([]);
  const [testClient, setTestClient] = useState("");
  const [busy, setBusy] = useState("");
  const [log, setLog] = useState([]);

  const load = async () => {
    setBusy("load");
    try {
      const [settings, logs, clientList] = await Promise.all([
        api.get("/emails/settings"),
        api.get("/emails/log?limit=20"),
        api.get("/clients"),
      ]);
      setConfig(settings.data);
      setTrainingDays((settings.data.treinamento_dias_aviso || []).join(", "));
      setPlanDays((settings.data.plano_acao_dias_aviso || []).join(", "));
      setLog(logs.data?.events || []);
      const withEmail = (Array.isArray(clientList.data) ? clientList.data : clientList.data?.items || []).filter((c) => (c.email || "").includes("@"));
      setClients(withEmail);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar as configurações de e-mail");
    } finally { setBusy(""); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (patch = {}) => {
    setBusy("save");
    try {
      const body = {
        enabled: config.enabled,
        templates: config.templates,
        treinamento_dias_aviso: parseDays(trainingDays),
        plano_acao_dias_aviso: parseDays(planDays),
        ...patch,
      };
      const r = await api.put("/emails/settings", body);
      setConfig((prev) => ({ ...prev, ...r.data }));
      setTrainingDays((r.data.treinamento_dias_aviso || []).join(", "));
      setPlanDays((r.data.plano_acao_dias_aviso || []).join(", "));
      toast.success("Configuração de e-mails salva");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar a configuração");
    } finally { setBusy(""); }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const r = await api.post(`/emails/clients/${testClient}/test`);
      toast.success(`E-mail de teste enviado para ${r.data.to}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar o e-mail de teste");
    } finally { setBusy(""); }
  };

  const selected = useMemo(() => clients.find((c) => c.id === testClient), [clients, testClient]);

  if (!config) {
    return <Card className="border-border/75"><CardContent className="p-6 text-sm text-muted-foreground">Carregando configurações de e-mail…</CardContent></Card>;
  }

  return (
    <Card id="emails" data-testid="email-automation-panel" className="border-border/75">
      <CardContent className="space-y-6 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex gap-3">
            <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Mail className="size-5" /></div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-semibold">E-mails automáticos</h2>
                <Badge data-testid="email-master-status" variant={config.enabled ? "secondary" : "outline"}>{config.enabled ? "Ativo" : "Desativado"}</Badge>
              </div>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
                Remetente <strong>{config.from_name}</strong>{config.reply_to ? <> · respostas vão para <strong>{config.reply_to}</strong></> : null}.
                Os destinatários são sempre o e-mail cadastrado do cliente; nenhum envio acontece enquanto a chave geral estiver desligada. O histórico mostra quando o cliente abriu o e-mail e clicou no link.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="email-master-toggle" className="text-xs text-muted-foreground">Envio geral</Label>
            <Switch id="email-master-toggle" data-testid="email-master-toggle" checked={config.enabled}
              onCheckedChange={(checked) => { setConfig((p) => ({ ...p, enabled: checked })); save({ enabled: checked }); }} />
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {Object.entries(KIND_LABELS).map(([kind, meta]) => (
            <div key={kind} className="flex items-start justify-between gap-3 rounded-xl border border-border/75 p-4">
              <div>
                <div className="text-sm font-medium">{meta.title}</div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{meta.text}</p>
              </div>
              <Switch data-testid={`email-toggle-${kind}`} checked={Boolean(config.templates?.[kind])}
                onCheckedChange={(checked) => {
                  const templates = { ...config.templates, [kind]: checked };
                  setConfig((p) => ({ ...p, templates }));
                  save({ templates });
                }} />
            </div>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="label-tech" htmlFor="email-training-days">Avisar treinamento com quantos dias de antecedência</Label>
            <Input id="email-training-days" data-testid="email-training-days" value={trainingDays}
              onChange={(e) => setTrainingDays(e.target.value)} placeholder="Ex.: 90, 30" />
            <p className="text-xs text-muted-foreground">Um e-mail por prazo informado. Deixe vazio para não enviar automaticamente.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="label-tech" htmlFor="email-plan-days">Avisar plano de ação com quantos dias de antecedência</Label>
            <Input id="email-plan-days" data-testid="email-plan-days" value={planDays}
              onChange={(e) => setPlanDays(e.target.value)} placeholder="Ex.: 15, 5" />
            <p className="text-xs text-muted-foreground">Considera o prazo das ações pendentes do plano.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border/75 p-4">
          <div className="min-w-[240px] flex-1 space-y-1.5">
            <Label className="label-tech" htmlFor="email-test-client">Enviar teste para o e-mail de um cliente</Label>
            <select id="email-test-client" data-testid="email-test-client" value={testClient}
              onChange={(e) => setTestClient(e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm">
              <option value="">{clients.length === 0 ? "Nenhum cliente com e-mail cadastrado" : "Selecione o cliente destinatário"}</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.razao_social} — {c.email}</option>)}
            </select>
            {selected && <p className="text-xs text-muted-foreground">O teste vai para {selected.email}</p>}
          </div>
          <Button data-testid="email-send-test" type="button" variant="outline" onClick={sendTest} disabled={!testClient || busy === "test"}>
            {busy === "test" ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Enviar teste
          </Button>
          <Button data-testid="email-save" type="button" onClick={() => save()} disabled={busy === "save"}>
            {busy === "save" ? <Loader2 className="size-4 animate-spin" /> : null} Salvar prazos
          </Button>
        </div>

        <div className="rounded-xl border border-border/75 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium">Últimos envios</div>
            <Button data-testid="email-log-refresh" type="button" variant="ghost" size="sm" onClick={load} disabled={busy === "load"}>Atualizar</Button>
          </div>
          {log.length === 0 ? (
            <p data-testid="email-log-empty" className="mt-2 text-xs text-muted-foreground">Nenhum e-mail enviado ainda.</p>
          ) : (
            <ul data-testid="email-log-list" className="mt-3 space-y-2">
              {log.map((event, index) => (
                <li key={`${event.sent_at}-${index}`} data-testid={`email-log-${index}`} className="rounded-lg bg-muted/25 p-3 text-xs">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={event.status === "sent" ? "secondary" : "outline"}>{event.status === "sent" ? "enviado" : "erro"}</Badge>
                    <span className="font-mono text-primary">{event.kind}</span>
                    <span className="text-muted-foreground">{new Date(event.sent_at).toLocaleString("pt-BR")}</span>
                    {event.open_count > 0 && (
                      <Badge data-testid={`email-log-${index}-opened`} variant="outline" className="border-emerald-500/40 text-emerald-600">
                        <Eye className="mr-1 size-3" />Aberto {event.open_count > 1 ? `${event.open_count}x` : ""} · {new Date(event.opened_at).toLocaleString("pt-BR")}
                      </Badge>
                    )}
                    {event.click_count > 0 && (
                      <Badge data-testid={`email-log-${index}-clicked`} variant="outline" className="border-primary/40 text-primary">
                        <MousePointerClick className="mr-1 size-3" />Clicou {event.click_count > 1 ? `${event.click_count}x` : ""} · {new Date(event.clicked_at).toLocaleString("pt-BR")}
                      </Badge>
                    )}
                    {event.status === "sent" && !event.open_count && (
                      <span data-testid={`email-log-${index}-pending`} className="text-muted-foreground">sem abertura registrada</span>
                    )}
                  </div>
                  <div className="mt-1 break-all text-muted-foreground">{event.to} · {event.subject}{event.error ? ` · ${event.error}` : ""}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
